import React from "react";
import { Play, Square, RefreshCw, Settings as SettingsIcon, Activity, Globe2, BookOpen, Brain, LogOut, History, Layers, Server, Grid2x2 } from "lucide-react";
import { StatusPill } from "./StatusPill";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { baseAsset } from "@/lib/utils";

const fmtNum = (n, opts = {}) =>
    n === null || n === undefined || Number.isNaN(n)
        ? "--"
        : Number(n).toLocaleString("en-US", { maximumFractionDigits: 4, ...opts });

const fmtUptime = (ms) => {
    if (!ms || ms < 0) return "--";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${ss}s`;
    return `${ss}s`;
};

export const Header = ({
    status,
    onStart,
    onStopRequest,
    onModeToggleRequest,
    onNetworkToggleRequest,
    onOpenSettings,
    onForceRecover,
    onLogout,
}) => {
    const isRunning = !!status?.running;
    const isLive = status?.mode === "live";
    const tickAge = status?.tick_age_ms;
    // Smoothed inter-tick cadence (EMA) — stable, unlike the raw sampled age.
    // Fall back to raw age only if the EMA hasn't warmed up yet.
    const tickCadence = status?.tick_interval_ms ?? tickAge;
    const tickStale = tickAge != null && tickAge > 5000;
    const tickColor = tickCadence == null || tickStale
        ? (tickStale ? "#FF5C6C" : "#6F7D92")
        : tickCadence < 1500 ? "#2BE38B" : tickCadence < 4000 ? "#FFB020" : "#FF5C6C";

    return (
        <div className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(11,15,20,0.85)", borderBottom: "1px solid #1B2A3A" }}>
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3">
                {/* Brand */}
                <div className="flex items-center gap-2.5 shrink-0">
                    <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: "linear-gradient(135deg, rgba(46,230,214,0.25), rgba(61,167,255,0.15))", border: "1px solid rgba(46,230,214,0.3)" }}
                    >
                        <Activity size={14} color="#2EE6D6" />
                    </div>
                    <div className="leading-tight">
                        <div className="text-sm font-semibold text-[#E6EDF6] whitespace-nowrap">AsterDex GridDesk</div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6F7D92] whitespace-nowrap">{(status?.config?.symbol || status?.symbol || "BTCUSDT")} · Trailing Self-Heal</div>
                    </div>
                </div>

                <div className="hidden md:block h-6 w-px bg-[#1B2A3A] mx-1 shrink-0" />

                {/* Status pills */}
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                    <StatusPill
                        variant={isRunning ? "running" : "stopped"}
                        withDot={isRunning}
                        testId="header-bot-status-pill"
                    >
                        {isRunning ? "Running" : "Stopped"}
                    </StatusPill>
                    <StatusPill variant={isLive ? "live" : "paper"} testId="header-mode-pill">
                        {isLive ? "LIVE" : "PAPER"}
                    </StatusPill>
                    <button
                        onClick={() => !status?.running && onNetworkToggleRequest(status?.use_testnet ? "mainnet" : "testnet")}
                        disabled={!!status?.running}
                        title={status?.running ? "Stop bot to switch network" : (status?.use_testnet ? "Click to switch to MAINNET" : "Click to switch to TESTNET")}
                        className="btn-anim"
                        data-testid="header-network-button"
                    >
                        <StatusPill
                            variant={status?.use_testnet ? "info" : "accent"}
                            testId="header-network-pill"
                        >
                            <Globe2 size={10} className="-ml-0.5" />
                            {status?.use_testnet ? "TESTNET" : "MAINNET"}
                        </StatusPill>
                    </button>
                    {status?.kill_switch && (
                        <StatusPill variant="live" withDot testId="header-killswitch-pill">
                            Kill-Switch
                        </StatusPill>
                    )}
                </div>

                <div className="flex-1" />

                {/* Metrics */}
                <div className="hidden lg:flex items-center gap-5 px-3 shrink-0">
                    <div className="flex flex-col items-end leading-tight min-w-[88px]">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92]">{baseAsset(status?.config?.symbol)}</div>
                        <div className="font-mono text-sm text-[#E6EDF6] tabular-nums" data-testid="header-btc-balance">
                            {fmtNum(status?.balances?.btc_free, { maximumFractionDigits: 6 })}
                        </div>
                    </div>
                    <div className="flex flex-col items-end leading-tight min-w-[104px]">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92]">USDT</div>
                        <div className="font-mono text-sm text-[#E6EDF6] tabular-nums" data-testid="header-usdt-balance">
                            ${fmtNum(status?.balances?.usdt_free, { maximumFractionDigits: 2 })}
                        </div>
                    </div>
                    <div className="flex flex-col items-end leading-tight min-w-[72px]">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92]">Uptime</div>
                        <div className="font-mono text-sm text-[#E6EDF6] tabular-nums" data-testid="header-uptime">
                            {isRunning ? fmtUptime(status?.uptime_ms) : "—"}
                        </div>
                    </div>
                    <div className="flex flex-col items-end leading-tight min-w-[72px]">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#6F7D92]">Tick</div>
                        <div className="font-mono text-sm tabular-nums" style={{ color: tickColor }} data-testid="header-latency">
                            {tickStale ? "stale" : (tickCadence != null ? `${tickCadence} ms` : "—")}
                        </div>
                    </div>
                </div>

                {/* Mode toggle */}
                <div className="flex items-center gap-2 pr-2 shrink-0">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">PAPER</span>
                    <Switch
                        checked={isLive}
                        onCheckedChange={() => onModeToggleRequest(isLive ? "paper" : "live")}
                        data-testid="mode-toggle-switch"
                    />
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[#FFB020]">LIVE</span>
                </div>

                {/* Buttons — wrap onto a second row on narrow desktops rather
                    than overflowing (which previously forced hiding nav items). */}
                <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
                    {!isRunning ? (
                        <Button
                            onClick={onStart}
                            className="btn-anim bg-[#2EE6D6] hover:bg-[#43F0E0] text-[#0B0F14] gap-1.5"
                            data-testid="bot-start-button"
                        >
                            <Play size={14} /> Start
                        </Button>
                    ) : (
                        <Button
                            onClick={onStopRequest}
                            className="btn-anim bg-[#FF5C6C] hover:bg-[#FF7080] text-[#0B0F14] gap-1.5"
                            data-testid="bot-stop-button"
                        >
                            <Square size={14} /> Stop
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={onForceRecover}
                        className="btn-anim border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] gap-1.5"
                        data-testid="force-recover-button"
                    >
                        <RefreshCw size={14} /> Recover
                    </Button>
                    <Button
                        variant="outline"
                        onClick={onOpenSettings}
                        className="btn-anim border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] gap-1.5"
                        data-testid="settings-open-button"
                    >
                        <SettingsIcon size={14} /> Settings
                    </Button>
                    <a
                        href="#/history"
                        className="btn-anim inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12px] font-medium border border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] transition-colors"
                        data-testid="history-open-link"
                    >
                        <History size={14} /> History
                    </a>
                    <a
                        href="#/group-sell"
                        className="btn-anim inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12px] font-medium border border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] transition-colors"
                        data-testid="groupsell-open-link"
                    >
                        <Layers size={14} /> Group Sell
                    </a>
                    <a
                        href="#/grids"
                        className="btn-anim inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12px] font-medium border border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] transition-colors"
                        data-testid="grids-open-link"
                    >
                        <Grid2x2 size={14} /> Grids
                    </a>
                    <a
                        href="#/smartgrid"
                        className="btn-anim inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12px] font-medium transition-colors"
                        style={{ background: "linear-gradient(135deg, rgba(46,230,214,0.18), rgba(61,167,255,0.12))", border: "1px solid rgba(46,230,214,0.35)", color: "#2EE6D6" }}
                        data-testid="smartgrid-open-link"
                    >
                        <Brain size={14} /> SmartGrid AI
                    </a>
                    <a
                        href="#/server"
                        className="btn-anim inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12px] font-medium border border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] transition-colors"
                        data-testid="server-open-link"
                    >
                        <Server size={14} /> Server
                    </a>
                    <a
                        href="#/docs"
                        className="btn-anim inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12px] font-medium border border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] transition-colors"
                        data-testid="docs-open-link"
                    >
                        <BookOpen size={14} /> Docs
                    </a>
                    <Button
                        variant="outline"
                        onClick={onLogout}
                        className="btn-anim border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#FF5C6C] gap-1.5"
                        data-testid="logout-button"
                    >
                        <LogOut size={14} /> Logout
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Header;
